import { useEffect, useMemo, useState } from "react";

import { useHeaderProfile } from "../header/useHeaderProfile";

// ──────────────────────────────────────────────────────────────────────────
// Which transfer-window country the REQUESTS pages should open on.
//
// They used to open on the literal string "Spain"
// (useClubTenders / useStaffTenders / useCoachTenders), so a German club was
// shown the Spanish window as if it were its own. The country a club works in
// is a fact the platform already has; it was simply never asked for.
//
// WHERE THE COUNTRY REALLY COMES FROM
//   profile -> club_details.country
// `club_details` is the club payload on the role's own profile endpoint
// (/users/profile/ for ClubAdmin, /users/profile/staff/, /users/profile/coach/)
// and is serialized by the backend's ClubSerializer. Its `country` is the
// club's own, resolved server-side: the explicit Club.country when the club has
// one, otherwise the Wyscout area the club is linked to — which is where the
// value actually lives for clubs that signed up through the normal flow, and
// the same source the request cards already display.
//
// WHY IT IS MATCHED AGAINST THE DROPDOWN LIST
// The window endpoints match their `country` parameter as an EXACT string
// against the TransferWindow table, whose spellings come from the FIFA member
// association list. A club country is a name from a different vocabulary, so it
// is only adopted when it is actually one of the countries the window API
// offers (the very list the dropdown is built from). The list's own spelling is
// what gets returned, so the follow-up request cannot miss on casing.
//
// WHAT HAPPENS WHEN IT CANNOT BE RESOLVED: NOTHING. The hook returns "", the
// pages start with no country selected, and the status line renders its empty
// placeholder instead of a window. Showing no window beats showing a foreign
// one. The dropdown still works, so the user can always pick a country.
// (Same contract the wizard's schedule modal already follows: it says only what
// is actually known.)
// ──────────────────────────────────────────────────────────────────────────

// A country as it may arrive from the API: a bare name, or the small
// {name, code} object the platform uses elsewhere. Same normalisation as
// ClubPicker's `countryName`.
export const countryNameOf = (country) => {
  if (!country) return "";
  if (typeof country === "object") {
    return String(country.name || country.country || country.country_name || "").trim();
  }
  return String(country).trim();
};

const normalise = (value) => countryNameOf(value).toLowerCase().replace(/\s+/g, " ");

// The dropdown-list entry whose name IS this country, or "" — never a guess.
// Deliberately name-only: resolving through an ISO code would collapse England,
// Scotland, Wales and Northern Ireland onto "GB" (see utils/countryFlag), and a
// Scottish club being shown the English window is the same class of bug this
// hook exists to remove.
export const matchWindowCountry = (clubCountry, countries) => {
  const wanted = normalise(clubCountry);
  if (!wanted || !Array.isArray(countries)) return "";
  const hit = countries.find(
    (entry) => entry && !entry.isViewAll && normalise(entry.name) === wanted
  );
  return hit ? hit.name : "";
};

// Read the club country off whichever profile shape the current role returns.
export const clubCountryFromProfile = (profileData) =>
  countryNameOf(
    profileData?.club_details?.country ||
      profileData?.data?.club_details?.country ||
      null
  );

/**
 * @param {Array} countries the transfer-window country list the page already
 *   fetched (`[{name, flag, isViewAll}]`). Matching happens against it, so the
 *   hook adds no second vocabulary and no second request for it.
 * @returns {string} the club's country in the list's own spelling, or "" when
 *   it is unknown or is not a country the window API serves.
 */
export const useClubWindowCountry = (countries) => {
  // Reuses the existing role-aware profile hook rather than repeating the
  // role -> endpoint mapping a second time, where the two copies could drift.
  // It is the one place that knows ClubAdmin -> /users/profile/, Staff ->
  // /users/profile/staff/, Coach -> /users/profile/coach/.
  const { profileData } = useHeaderProfile();

  const clubCountry = useMemo(
    () => clubCountryFromProfile(profileData),
    [profileData]
  );
  return useMemo(
    () => matchWindowCountry(clubCountry, countries),
    [clubCountry, countries]
  );
};

/**
 * Adopt the club's own country as the initial selection, once.
 *
 * "Once" matters: the moment the user picks a country from the dropdown their
 * choice must stand, so this only ever fills an EMPTY selection and then stops.
 */
export const useClubWindowCountryDefault = (countries, selected, setSelected) => {
  const country = useClubWindowCountry(countries);
  const [adopted, setAdopted] = useState(false);

  useEffect(() => {
    if (adopted || selected || !country) return;
    setAdopted(true);
    setSelected(country);
  }, [adopted, selected, country, setSelected]);
};

export default useClubWindowCountry;
